import type { CSSProperties } from "react";
import type { AlbumManifest, CoverTitle } from "../data/manifest";
import { cssFilter } from "../data/filters";
import { autoTitleHex, coverColorHex } from "../data/coverPalette";
import "../render/PageRenderer.css";

// Cover trim is the 210mm square; sizePt is in cover points (210mm ≈ 595.28pt),
// so title font-size = (sizePt / 595.28) of the page width. Container-query
// units (cqw) make that resolution-independent — the pb-cover is a container.
const TRIM_PT = (210 * 72) / 25.4; // 595.276

// Exact print constants (src/render/cover.ts): plate padding ratios ×sizePt,
// radius ratio ×plateHeight; shadow #000 @40%, offset +2pt down, blur ~3pt.
const PLATE_PAD_H = 0.45; // ×sizePt  == 0.45em (font-size == sizePt)
const PLATE_PAD_V = 0.28; // ×sizePt  == 0.28em
const PLATE_RADIUS = 0.3; // ×plateHeight
const SHADOW_OFFSET_PT = 2;
const SHADOW_BLUR_PT = 3;
const SHADOW_OPACITY = 0.4;
// Approx line box height (em) for the plate — print uses the TTF's
// ascent+descent (heightAtSize w/ descender); ~1.0 here, the residual metric.
const LINE_H_EM = 1;

// fontId families are self-hosted (styles/fonts.css) — the exact print TTFs.
const GENERIC: Record<string, string> = {
  bebas: "sans-serif",
  fraunces: "serif",
  playfair: "serif",
  kaushan: "cursive",
  inter: "sans-serif",
};
function fontFamily(fontId: string): string {
  return `"${fontId}", ${GENERIC[fontId] ?? "serif"}`;
}

/** cqw length for a point value against the cover trim (595.28pt). */
function ptCqw(pt: number): string {
  return `calc(${((pt / TRIM_PT) * 100).toFixed(4)} * 1cqw)`;
}

/** Resolve the title colour: explicit palette id / hex, or the §14.5 Auto rule
 *  (WCAG-max sentinel over the plate/colour backing). */
function titleColor(title: CoverTitle, bgColorId: string | null): string {
  if (title.color !== "auto") return coverColorHex(title.color);
  const backing = title.plate?.enabled
    ? coverColorHex(title.plate.colorId)
    : bgColorId
      ? coverColorHex(bgColorId)
      : null;
  // Over a photo with no plate there is no single backing hex — default to the
  // light sentinel + shadow (matches the app's over-photo treatment).
  return backing ? autoTitleHex(backing) : "#F4EFE7";
}

function TitleOverlay({
  title,
  bgColorId,
}: {
  title: CoverTitle;
  bgColorId: string | null;
}) {
  const color = titleColor(title, bgColorId);
  const hasPlate = Boolean(title.plate?.enabled);

  // Plate height in em (font-size == sizePt): line box + 2× vertical pad.
  const plateHEm = LINE_H_EM + 2 * PLATE_PAD_V;
  const radiusEm = PLATE_RADIUS * plateHEm; // border-radius auto-clamps to <=half

  // Block CENTRE on position.{x,y} (print centres the plate / cap-band on it).
  const wrapStyle: CSSProperties = {
    position: "absolute",
    left: `${title.position.x * 100}%`,
    top: `${title.position.y * 100}%`,
    transform: "translate(-50%, -50%)",
    textAlign: title.align,
  };
  const textStyle: CSSProperties = {
    display: "inline-block",
    fontFamily: fontFamily(title.fontId),
    fontWeight: title.weight,
    fontSize: ptCqw(title.sizePt),
    lineHeight: LINE_H_EM,
    whiteSpace: "nowrap", // single line, never wrap (print convention)
    color,
    textShadow: title.shadow
      ? `0 ${ptCqw(SHADOW_OFFSET_PT)} ${ptCqw(SHADOW_BLUR_PT)} rgba(0,0,0,${SHADOW_OPACITY})`
      : "none",
    ...(hasPlate
      ? {
          background: coverColorHex(title.plate!.colorId),
          // exact ratios ×sizePt == ×em (font-size == sizePt)
          padding: `${PLATE_PAD_V}em ${PLATE_PAD_H}em`,
          borderRadius: `${radiusEm}em`,
        }
      : null),
  };
  return (
    <div style={wrapStyle} aria-hidden="true">
      <span style={textStyle}>{title.text}</span>
    </div>
  );
}

/** Inside-front-cover solid (the left board after the cover opens). A colour
 *  cover keeps its own fill (a matching colour endpaper reads intentional); a
 *  photo/pattern cover uses a warm cream endpaper — NOT near-black, which read
 *  as a broken black page (Samuel). Deviates from the print spine-solid on
 *  purpose: the web inside-cover is a premium endpaper. */
const ENDPAPER = "#EFE7D6";
export function coverBackHex(cover: AlbumManifest["cover"]): string {
  return cover.background.type === "color"
    ? coverColorHex(cover.background.colorId)
    : ENDPAPER;
}

/** The closed album cover as a printed object — square, hard shadow, square
 *  corners. Photo/color background + the baked §6 title. coverLook applies to
 *  the cover PHOTO only (never title/plate/pattern chrome). */
export function CoverArt({ manifest }: { manifest: AlbumManifest }) {
  const { cover, coverLook, photos } = manifest;
  const bg = cover.background;
  const bgColorId = bg.type === "color" ? bg.colorId : null;

  let backdrop: JSX.Element;
  if (bg.type === "photo") {
    const photo = photos[bg.photoId];
    const crop = bg.crop;
    const w = crop.w > 0 ? crop.w : 1;
    const h = crop.h > 0 ? crop.h : 1;
    const imgStyle: CSSProperties = {
      width: `${(1 / w) * 100}%`,
      height: `${(1 / h) * 100}%`,
      left: `${(-crop.x / w) * 100}%`,
      top: `${(-crop.y / h) * 100}%`,
      filter: cssFilter(coverLook), // photo only
    };
    backdrop = (
      <div className="pb-slot" style={{ inset: 0 }}>
        {photo ? (
          <img
            className="pb-photo"
            style={imgStyle}
            src={photo.srcset.page}
            alt={photo.alt ?? "Album cover"}
            draggable={false}
            decoding="async"
            fetchPriority="high"
          />
        ) : (
          <div className="pb-photo-missing" />
        )}
      </div>
    );
  } else {
    // color (or pattern — pattern asset render deferred, show its fill/paper).
    const fill = bg.type === "color" ? coverColorHex(bg.colorId) : "var(--page)";
    backdrop = <div className="pb-slot" style={{ inset: 0, background: fill }} />;
  }

  return (
    <div className="pb-page pb-cover">
      {backdrop}
      {cover.showTitle && cover.title.text ? (
        <TitleOverlay title={cover.title} bgColorId={bgColorId} />
      ) : null}
    </div>
  );
}
